import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import Reveal from "./Reveal";
import { useT } from "@/hooks/useT";
import {
  ArrowRight,
  Play,
  TrendingUp,
  Calendar,
  BedDouble,
  Users,
  CheckCircle2,
  Sparkles,
  Star,
  Activity,
  CircleDot,
} from "lucide-react";

/**
 * Premium cinematic SaaS hero — dark navy ecosystem.
 * Left: editorial copy + CTA + social proof.
 * Right: floating dashboard ecosystem (PMS, calendar, OTA syncs)
 *        connected with subtle dotted SVG lines and orange glow.
 *
 * No stock photography. No generic split layout.
 * Inspired by Cloudbeds, Mews, Stripe, Linear.
 */
export default function HeroImmersive() {
  const t = useT();
  return (
    <section
      className="relative isolate overflow-hidden bg-[#071326]"
      data-testid="hero-section"
    >
      {/* ─── CINEMATIC BACKGROUND ────────────────────────────────────── */}
      {/* Layered deep-blue radial gradients for depth */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#0F2647_0%,#071326_55%,#040A18_100%)]"
        aria-hidden
      />
      {/* Top-right warm orange glow */}
      <div
        className="pointer-events-none absolute -top-40 right-[-12%] h-[820px] w-[820px] rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.32),transparent_70%)] blur-3xl"
        aria-hidden
      />
      {/* Bottom-left subtle blue glow */}
      <div
        className="pointer-events-none absolute bottom-[-25%] left-[-12%] h-[640px] w-[640px] rounded-full bg-[radial-gradient(closest-side,rgba(56,109,212,0.22),transparent_70%)] blur-3xl"
        aria-hidden
      />
      {/* Subtle starfield particles */}
      <Particles />

      <div className="container-akm relative">
        <div className="grid items-center gap-14 py-20 md:py-24 lg:grid-cols-[1.05fr_1.1fr] lg:gap-12 xl:gap-16">
          {/* ────────────── LEFT ────────────── */}
          <div className="relative">
            <Reveal variant="left" delay={0}>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-300/90 backdrop-blur-sm"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={12} className="text-[#FF9A3D]" />
              {t("Platforma #1 për hotele në Shqipëri", "Albania's #1 hotel platform")}
            </span>
            </Reveal>

            <Reveal variant="left" delay={120}>
            <h1
              className="mt-7 font-display font-extrabold tracking-[-0.045em] text-white"
              style={{
                fontSize: "clamp(2.25rem, 1.0rem + 2.6vw, 3.75rem)",
                lineHeight: 1.06,
              }}
              data-testid="hero-title"
            >
              {t("Program modern për", "Software built for")}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#FFB875] via-[#FF8A3D] to-[#FF7A00] bg-clip-text text-transparent">
                  {t("menaxhimin e hotelit", "modern hotel management")}
                </span>
                <span
                  className="absolute -bottom-2 left-0 h-[14px] w-full rounded-full bg-[#FF7A00]/20 blur-xl"
                  aria-hidden
                />
              </span>
            </h1>
            </Reveal>

            <Reveal variant="left" delay={240}>
            <p
              className="mt-7 max-w-xl text-[16.5px] leading-[1.75] text-white/65"
              data-testid="hero-subtitle"
            >
              {t(
                "Rezervime, PMS, booking engine, channel manager dhe sinkronizim me ",
                "Reservations, PMS, booking engine, channel manager and instant sync with "
              )}
              <span className="text-white/90">Booking.com</span>,{" "}
              <span className="text-white/90">Airbnb</span>{t(" dhe ", " and ")}
              <span className="text-white/90">Expedia</span>{t(" — në një platformë të vetme moderne.", " — all in one modern platform.")}
            </p>
            </Reveal>

            {/* CTA */}
            <Reveal variant="up" delay={360}>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/demo"
                data-testid="hero-demo-cta"
                className="group relative inline-flex items-center gap-2.5 overflow-hidden rounded-full bg-[#FF7A00] px-7 py-4 text-[13px] font-semibold tracking-tight text-white shadow-[0_18px_44px_-12px_rgba(255,122,0,0.65)] transition-all hover:bg-[#EA580C] hover:shadow-[0_22px_54px_-12px_rgba(234,88,12,0.85)]"
              >
                <span className="relative z-10">{t("Kërko Demo", "Request Demo")}</span>
                <ArrowRight
                  size={15}
                  className="relative z-10 transition-transform group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                to="/platforma"
                data-testid="hero-platform-cta"
                className="group inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.04] px-7 py-4 text-[13px] font-semibold tracking-tight text-white backdrop-blur-sm transition-all hover:border-white/30 hover:bg-white/[0.08]"
              >
                <Play size={11} className="fill-white text-white" />
                {t("Shiko Platformën", "See the Platform")}
              </Link>
            </div>
            </Reveal>

            {/* Social proof */}
            <Reveal variant="up" delay={500}>
            <div
              className="mt-10 flex items-center gap-4"
              data-testid="hero-social-proof"
            >
              <div className="flex -space-x-2.5">
                {[
                  "from-[#F26322] to-[#FF9A3D]",
                  "from-[#3B82F6] to-[#1D4ED8]",
                  "from-[#10B981] to-[#047857]",
                  "from-[#A855F7] to-[#6D28D9]",
                ].map((g, i) => (
                  <span
                    key={i}
                    className={`grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br ${g} font-display text-[12px] font-bold text-white ring-2 ring-[#071326]`}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                ))}
                <span className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-[10px] font-bold text-white ring-2 ring-[#071326] backdrop-blur-sm">
                  +
                </span>
              </div>
              <div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      size={12}
                      className="fill-[#FFB875] text-[#FFB875]"
                    />
                  ))}
                  <span className="ml-1.5 text-[12px] font-semibold text-white">
                    4.9
                  </span>
                </div>
                <div className="text-[12px] text-white/55">
                  {t("Besuar nga 115+ hotele, vila dhe apartamente", "Trusted by 115+ hotels, villas and apartments")}
                </div>
              </div>
            </div>
            </Reveal>
          </div>

          {/* ────────────── RIGHT — Ecosystem of floating cards ────────────── */}
          <Reveal variant="right" delay={200} duration={900}>
            <Ecosystem t={t} />
          </Reveal>
        </div>
      </div>

      {/* Soft bottom edge fade for transition to next section */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#040A18]"
        aria-hidden
      />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Subtle particles — pure CSS dots
   ───────────────────────────────────────────────────────────────────── */
function Particles() {
  const dots = [
    { l: "8%", t: "20%", s: 2, o: 0.35 },
    { l: "16%", t: "70%", s: 1.5, o: 0.25 },
    { l: "32%", t: "12%", s: 1.5, o: 0.3 },
    { l: "44%", t: "80%", s: 2, o: 0.4 },
    { l: "58%", t: "18%", s: 1.5, o: 0.28 },
    { l: "72%", t: "62%", s: 2, o: 0.35 },
    { l: "88%", t: "26%", s: 1.5, o: 0.3 },
    { l: "92%", t: "78%", s: 2, o: 0.32 },
  ];
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: d.l,
            top: d.t,
            width: `${d.s}px`,
            height: `${d.s}px`,
            opacity: d.o,
            boxShadow: `0 0 ${d.s * 4}px rgba(255,255,255,0.4)`,
          }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   ECOSYSTEM — floating dashboard cards with SVG connection lines
   ───────────────────────────────────────────────────────────────────── */
function Ecosystem({ t }) {
  return (
    <div className="relative" data-testid="hero-ecosystem">
      <div className="relative mx-auto aspect-[10/10] w-full max-w-[640px]">
        {/* Background spotlight behind the ecosystem */}
        <div
          className="absolute inset-[8%] -z-10 rounded-[40px] bg-[radial-gradient(closest-side,rgba(255,138,61,0.18),transparent_70%)] blur-2xl"
          aria-hidden
        />

        {/* Connection lines (dotted) drawn between cards */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 110"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="line-grad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#FF9A3D" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#FF9A3D" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* PMS ↔ Calendar */}
          <path
            d="M 32 38 Q 50 50 60 75"
            fill="none"
            stroke="url(#line-grad)"
            strokeWidth="0.25"
            strokeDasharray="0.8 1.2"
          />
          {/* PMS ↔ Booking sync */}
          <path
            d="M 60 26 Q 75 30 85 38"
            fill="none"
            stroke="url(#line-grad)"
            strokeWidth="0.25"
            strokeDasharray="0.8 1.2"
          />
          {/* Booking ↔ Airbnb */}
          <path
            d="M 88 50 Q 90 62 84 70"
            fill="none"
            stroke="url(#line-grad)"
            strokeWidth="0.25"
            strokeDasharray="0.8 1.2"
          />
          {/* Calendar ↔ Airbnb */}
          <path
            d="M 60 82 Q 72 80 80 72"
            fill="none"
            stroke="url(#line-grad)"
            strokeWidth="0.25"
            strokeDasharray="0.8 1.2"
          />
        </svg>

        {/* ─── Main PMS Dashboard (top-left, largest) ─── */}
        <GlassCard
          className="absolute left-0 top-0 w-[62%] max-w-[390px]"
          testId="hero-card-pms"
          accent
        >
          {/* Card header */}
          <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
            <div className="flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-[#FF9A3D] to-[#EA580C] font-display text-[11px] font-extrabold text-white shadow-[0_4px_12px_-2px_rgba(234,88,12,0.6)]">
                a
              </span>
              <div>
                <div className="font-display text-[11.5px] font-bold text-white">
                  Akomoda PMS
                </div>
                <div className="text-[9px] text-white/40">{t("Dashboard · Sot", "Dashboard · Today")}</div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-semibold text-emerald-300 ring-1 ring-emerald-400/20">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
              Live
            </span>
          </div>

          {/* KPI cards */}
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {[
              [t("Rezervime", "Bookings"),  "32",    "+12%", "from-[#FF9A3D]/20 to-[#EA580C]/0"],
              [t("Të Ardhura", "Revenue"),  "€8,4k", "+18%", "from-emerald-400/20 to-emerald-400/0"],
              [t("Zënë", "Occupied"),       "84%",   "+5%",  "from-sky-400/20 to-sky-400/0"],
            ].map(([k, v, d, g]) => (
              <div
                key={k}
                className={`relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.03] p-2 bg-gradient-to-br ${g}`}
              >
                <div className="text-[8.5px] font-medium text-white/45">{k}</div>
                <div className="mt-0.5 font-display text-[14px] font-extrabold leading-none text-white">
                  {v}
                </div>
                <div className="mt-1 text-[8px] font-semibold text-emerald-300">
                  ▲ {d}
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="mt-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-semibold text-white/70">
                {t("Rezervime — 7 ditët e fundit", "Bookings — last 7 days")}
              </span>
              <Activity size={10} className="text-[#FF9A3D]" />
            </div>
            <svg viewBox="0 0 240 60" className="mt-1 h-12 w-full">
              <defs>
                <linearGradient id="chart-grad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#FF9A3D" stopOpacity="0.45" />
                  <stop offset="100%" stopColor="#FF9A3D" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,46 C25,40 45,42 70,30 C95,18 115,34 140,22 C165,14 185,28 210,12 L240,4 L240,60 L0,60 Z"
                fill="url(#chart-grad)"
              />
              <path
                d="M0,46 C25,40 45,42 70,30 C95,18 115,34 140,22 C165,14 185,28 210,12 L240,4"
                fill="none"
                stroke="#FF9A3D"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <circle cx="240" cy="4" r="3.5" fill="#FF9A3D" />
              <circle cx="240" cy="4" r="6" fill="#FF9A3D" fillOpacity="0.25" />
            </svg>
          </div>

          {/* Recent reservations list */}
          <div className="mt-2 space-y-1">
            {[
              ["AM", "Anna Müller", t("Suita 304", "Suite 304"), "€642"],
            ].map(([i, n, r, p]) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-md bg-white/[0.02] px-2 py-1.5"
              >
                <span className="grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br from-white/15 to-white/5 text-[8.5px] font-bold text-white">
                  {i}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-[10px] font-semibold text-white">
                    {n}
                  </div>
                  <div className="text-[8.5px] text-white/40">{r}</div>
                </div>
                <div className="text-[10px] font-bold text-white">{p}</div>
                <CheckCircle2 size={11} className="text-emerald-400" />
              </div>
            ))}
          </div>
        </GlassCard>

        {/* ─── Calendar / Mobile booking card (bottom-left) ─── */}
        <GlassCard
          className="absolute bottom-[6%] left-[2%] w-[52%] max-w-[290px]"
          testId="hero-card-calendar"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar size={12} className="text-[#FF9A3D]" />
              <span className="text-[10.5px] font-semibold text-white">
                {t("Qershor 2026", "June 2026")}
              </span>
            </div>
            <span className="text-[9px] text-white/40">{t("12 dhoma", "12 rooms")}</span>
          </div>

          <div className="mt-2.5 grid grid-cols-7 gap-1 text-center text-[8px] font-medium text-white/40">
            {["H", "M", "M", "E", "P", "Sh", "D"].map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>

          <div className="mt-1.5 grid grid-cols-7 gap-1">
            {Array.from({ length: 21 }).map((_, i) => {
              const booked = [2, 3, 4, 8, 9, 13, 14, 15, 16, 18, 19, 20].includes(i);
              const today = i === 9;
              return (
                <div
                  key={i}
                  className={`grid h-5 place-items-center rounded text-[8px] font-semibold ${
                    today
                      ? "bg-gradient-to-br from-[#FF9A3D] to-[#EA580C] text-white shadow-[0_2px_6px_-1px_rgba(234,88,12,0.6)]"
                      : booked
                      ? "bg-[#FF9A3D]/15 text-[#FFB875] ring-1 ring-[#FF9A3D]/25"
                      : "bg-white/[0.04] text-white/50"
                  }`}
                >
                  {i + 1}
                </div>
              );
            })}
          </div>
        </GlassCard>

        {/* ─── Booking.com sync card (top-right) ─── */}
        <GlassCard
          className="absolute right-0 top-[4%] w-[40%] max-w-[230px]"
          testId="hero-card-booking"
        >
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#003580] font-display text-[11px] font-extrabold text-white shadow-[0_4px_12px_-2px_rgba(0,53,128,0.7)]">
              B.
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-semibold text-white">Booking.com</div>
              <div className="flex items-center gap-1 text-[8.5px] text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                {t("Sinkronizuar", "Synced")}
              </div>
            </div>
          </div>
          <div className="mt-2.5 grid grid-cols-2 gap-1.5">
            <div className="rounded-md bg-white/[0.03] p-1.5">
              <div className="text-[8px] text-white/45">{t("Rezervime", "Bookings")}</div>
              <div className="font-display text-[12px] font-extrabold text-white">
                14
              </div>
            </div>
            <div className="rounded-md bg-white/[0.03] p-1.5">
              <div className="text-[8px] text-white/45">{t("Të ardhura", "Revenue")}</div>
              <div className="font-display text-[12px] font-extrabold text-white">
                €3.2k
              </div>
            </div>
          </div>
        </GlassCard>

        {/* ─── Airbnb sync card (middle-right) ─── */}
        <GlassCard
          className="absolute right-[-3%] top-[34%] w-[42%] max-w-[240px]"
          testId="hero-card-airbnb"
        >
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-[#FF5A5F] to-[#E53E48] font-display text-[12px] font-extrabold text-white shadow-[0_4px_12px_-2px_rgba(229,62,72,0.7)]">
              A
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-semibold text-white">Airbnb</div>
              <div className="flex items-center gap-1 text-[8.5px] text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                {t("Sinkronizuar", "Synced")}
              </div>
            </div>
          </div>
          <div className="mt-2.5 flex items-center justify-between rounded-md bg-white/[0.03] p-1.5">
            <span className="text-[8.5px] text-white/55">{t("Çmime sot", "Rates today")}</span>
            <span className="font-display text-[11px] font-extrabold text-white">
              {t("€128 / natë", "€128 / night")}
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-[78%] rounded-full bg-gradient-to-r from-[#FF9A3D] to-[#FF5A5F]" />
            </div>
            <span className="text-[8.5px] font-semibold text-white">78%</span>
          </div>
        </GlassCard>

        {/* ─── Occupancy donut (bottom-right floating) ─── */}
        <GlassCard
          className="absolute bottom-[8%] right-[2%] w-[36%] max-w-[200px]"
          testId="hero-card-occupancy"
        >
          <div className="flex items-center justify-between">
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/45">
              {t("Zënë", "Occupied")}
            </span>
            <Users size={11} className="text-white/45" />
          </div>
          <div className="mt-2.5 flex items-center gap-2.5">
            <svg viewBox="0 0 42 42" className="h-14 w-14 -rotate-90">
              <circle
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="5"
              />
              <circle
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke="url(#donut-grad)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray="84 100"
              />
              <defs>
                <linearGradient id="donut-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FFB875" />
                  <stop offset="100%" stopColor="#EA580C" />
                </linearGradient>
              </defs>
            </svg>
            <div>
              <div className="font-display text-[20px] font-extrabold leading-none text-white">
                84%
              </div>
              <div className="mt-1 text-[9px] text-white/45">{t("Këtë javë", "This week")}</div>
            </div>
          </div>
        </GlassCard>

        {/* ─── Tiny live event chip (mid-floating) ─── */}
        <div
          className="absolute left-[44%] top-[64%] flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 backdrop-blur-md"
          data-testid="hero-chip-live"
        >
          <CircleDot size={10} className="text-emerald-300" />
          <span className="text-[9px] font-semibold text-emerald-200">
            {t("Rezervim i ri · Suita 304", "New booking · Suite 304")}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   GlassCard — dark glass with subtle inner ring + orange accent option
   ───────────────────────────────────────────────────────────────────── */
function GlassCard({ className = "", children, testId, accent = false }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-white/[0.08] bg-[rgba(13,28,52,0.72)] p-3 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.02)_inset] backdrop-blur-xl ${className}`}
    >
      {accent && (
        <span
          className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-b from-[#FF9A3D]/30 via-transparent to-transparent opacity-50 [mask:linear-gradient(black,transparent_30%)]"
          aria-hidden
        />
      )}
      <div className="relative">{children}</div>
    </div>
  );
}
